import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, EmptyState, Field, PageLoader, Segmented, TextField, Textarea, Toggle } from "@/components/ui";
import { DayPicker, IconPicker, KidChips } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction, useParentResponsibilities } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { describeDays, formatHHMM } from "@shared/time";
import { RESPONSIBILITY_TEMPLATES } from "@shared/templates";
import type { ResponsibilityDTO } from "@shared/types";

type Draft = Omit<ResponsibilityDTO, "id" | "sortOrder"> & { id?: string };

const empty: Draft = {
  title: "",
  description: null,
  icon: "✅",
  kidIds: [],
  frequency: "daily",
  daysOfWeek: 127,
  dueTime: null,
  active: true,
};

export function describeFrequency(r: Pick<ResponsibilityDTO, "frequency" | "daysOfWeek">) {
  if (r.frequency === "weekly") return "Once a week";
  if (r.frequency === "once") return "One time";
  return describeDays(r.daysOfWeek);
}

export function ResponsibilityForm({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const { kids } = useSession();
  return (
    <div className="space-y-5">
      <TextField
        label="Responsibility"
        placeholder="Make your bed"
        value={draft.title}
        maxLength={80}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
      />
      <Field label="Icon">
        <IconPicker value={draft.icon} onChange={(icon) => setDraft({ ...draft, icon })} />
      </Field>
      <Field label="Who">
        <KidChips kids={kids} value={draft.kidIds} onChange={(kidIds) => setDraft({ ...draft, kidIds })} allLabel="Every kid" />
      </Field>
      <Field label="Resets" hint="Responsibilities must be done each period before paid jobs unlock.">
        <Segmented
          value={draft.frequency}
          onChange={(frequency) => setDraft({ ...draft, frequency })}
          options={[
            { value: "daily", label: "Daily" },
            { value: "weekly", label: "Weekly" },
            { value: "once", label: "One time" },
          ]}
        />
      </Field>
      {draft.frequency === "daily" && (
        <Field label="On these days">
          <DayPicker value={draft.daysOfWeek} onChange={(daysOfWeek) => setDraft({ ...draft, daysOfWeek })} />
        </Field>
      )}
      <TextField
        label="Due by (optional)"
        type="time"
        value={draft.dueTime ?? ""}
        onChange={(e) => setDraft({ ...draft, dueTime: e.target.value || null })}
        hint="A reminder for kids — the gate resets at midnight either way."
      />
      <Field label="Details (optional)">
        <Textarea
          placeholder="What does “done” look like?"
          value={draft.description ?? ""}
          maxLength={500}
          onChange={(e) => setDraft({ ...draft, description: e.target.value || null })}
        />
      </Field>
      <Toggle checked={draft.active} onChange={(active) => setDraft({ ...draft, active })} label="Active" description="Paused responsibilities don't count toward the gate." />
    </div>
  );
}

export function ResponsibilitiesEditor() {
  const { kids, memberById } = useSession();
  const { data, isLoading } = useParentResponsibilities();
  const [draft, setDraft] = useState<Draft | null>(null);

  const save = useAction(
    (d: Draft) => {
      const { id, ...body } = d;
      return id ? api.patch(`/responsibilities/${id}`, body) : api.post("/responsibilities", body);
    },
    { success: "Saved", onSuccess: () => setDraft(null) },
  );
  const remove = useAction((id: string) => api.del(`/responsibilities/${id}`), {
    success: "Deleted",
    onSuccess: () => setDraft(null),
  });
  const addTemplate = useAction((t: (typeof RESPONSIBILITY_TEMPLATES)[number]) =>
    api.post("/responsibilities", { title: t.title, icon: t.icon, frequency: t.frequency, daysOfWeek: t.daysOfWeek ?? 127, dueTime: t.dueTime ?? null }),
  );

  if (isLoading || !data) return <PageLoader />;
  const list = data.responsibilities;
  const existing = new Set(list.map((r) => r.title.toLowerCase()));
  const suggestions = RESPONSIBILITY_TEMPLATES.filter((t) => !existing.has(t.title.toLowerCase())).slice(0, 6);

  const doneCount = (id: string) =>
    kids.filter((k) => data.board[k.id]?.items.find((i) => i.responsibility.id === id)?.counts).length;
  const dueKids = (r: ResponsibilityDTO) =>
    kids.filter((k) => data.board[k.id]?.items.find((i) => i.responsibility.id === r.id)?.due);

  return (
    <div>
      <PageHeader
        title="Responsibilities"
        subtitle="Non-negotiable. No reward — they unlock jobs."
        back="/parent/plan"
        actions={
          <Button size="sm" onClick={() => setDraft({ ...empty })}>
            <Plus className="size-4" /> Add
          </Button>
        }
      />

      {list.length === 0 ? (
        <EmptyState icon="🛏️" title="No responsibilities yet" action={<Button onClick={() => setDraft({ ...empty })}>Add the first one</Button>}>
          Responsibilities are the obligations every kid handles before they can earn.
        </EmptyState>
      ) : (
        <div className="mt-4 space-y-2">
          {list.map((r) => {
            const due = dueKids(r);
            return (
              <Card key={r.id} className="cursor-pointer p-3 transition hover:ring-brand/40" onClick={() => setDraft({ ...r })}>
                <div className="flex items-center gap-3">
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-sunken text-2xl">{r.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-extrabold">{r.title}</span>
                      {!r.active && <Badge>Paused</Badge>}
                    </div>
                    <div className="truncate text-sm text-muted">
                      {describeFrequency(r)}
                      {r.dueTime ? ` · by ${formatHHMM(r.dueTime)}` : ""} ·{" "}
                      {r.kidIds.length ? r.kidIds.map((id) => memberById(id)?.name).filter(Boolean).join(", ") : "Every kid"}
                    </div>
                  </div>
                  {r.active && due.length > 0 && (
                    <Badge tone={doneCount(r.id) === due.length ? "brand" : "neutral"}>
                      {doneCount(r.id)}/{due.length} today
                    </Badge>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 px-1 text-lg font-extrabold">Quick add</h2>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((t) => (
              <button
                key={t.title}
                type="button"
                onClick={() => addTemplate.mutate(t)}
                className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-2 text-sm font-bold shadow-card ring-1 ring-line transition hover:ring-brand"
              >
                <span>{t.icon}</span> {t.title}
                <Plus className="size-4 text-brand" />
              </button>
            ))}
          </div>
        </div>
      )}

      <Sheet
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Edit responsibility" : "New responsibility"}
        footer={
          <div className="flex gap-2">
            {draft?.id && (
              <Button variant="secondary" aria-label="Delete" onClick={() => draft.id && remove.mutate(draft.id)} loading={remove.isPending}>
                <Trash2 className="size-4 text-danger" />
              </Button>
            )}
            <Button block onClick={() => draft && save.mutate(draft)} loading={save.isPending} disabled={!draft?.title.trim()}>
              Save
            </Button>
          </div>
        }
      >
        {draft && <ResponsibilityForm draft={draft} setDraft={setDraft} />}
      </Sheet>
    </div>
  );
}
